import React, { useEffect, useState } from "react";
import { Box, Button, Grid2, Stack, ImageList, ImageListItem, ImageListItemBar, Typography, Tabs, Tab, useMediaQuery } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import { useDispatch } from "store";
import { useGetImageGalleryCategoryListQuery, useGetSubmittedImageGalleryQuery } from "store/slices/apis/imageGallerySlice";
import { API_END_POINT } from "config";
import PaginationComponent from "components/ui-components/PaginationComponent";
import moment from "moment";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { ReactComponent as CalendarIcon } from "assets/PwC_Funct_Icons_Calendar_Outline_Black_RGB.svg"

const StyledSubTab = styled(Tab)(({ theme }) => ({
    textTransform: "none", // Prevent uppercase
    fontWeight: "400",
    fontSize: "16px",
    color: `${theme?.customSettings?.themeColor}`,
    minHeight: '32px',
    minWidth: '0px',
    padding: '5px 10px 5px 10px',
    lineHeight: '24px',
    borderRadius: '10px',
    border: `1px solid ${theme?.customSettings?.themeColor}`,
    letterSpacing: '-0.4px',
    backgroundColor: '#F3F3F3',
    "&.Mui-selected": {
        color: "#fff",
        // borderRadius: '10px',
        backgroundColor: `${theme?.customSettings?.themeColor}`,
        // padding: '6px'
    }
}));

const SubmittedByMeImages = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const [pageNumber, setPageNumber] = useState(1);
    const [isImageGalleryApprove, setIsImageGalleryApprove] = useState(false);
    const [isImageGalleryReject, setIsImageGalleryReject] = useState(false);
    const { projectId } = useParams();
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [filters, setFilters] = useState({
        category: "",
        fromDate: "",
        toDate: "",
    });
    const [category, setCategory] = useState('');
    const [pendingList, setPendingList] = useState([]);
    const { data: categoryList } = useGetImageGalleryCategoryListQuery();
    const { data: pendingImageRes, isLoading: listLoading, isSuccess: imgSuccess, isError } = useGetSubmittedImageGalleryQuery({
        projectId: projectId,
        fromDate: filters?.fromDate,
        toDate: filters?.toDate,
        category: filters?.category,
        page: pageNumber,
        pageSize: 10,
    });
    const [open, setOpen] = useState(false);
    const [photoIndex, setPhotoIndex] = useState(0);
    const [selectedImage, setSelectedImage] = useState([]);
    const isXs = useMediaQuery(theme.breakpoints.only('xs'));
    const columns = isXs ? 1 : 4;

    const getImageUrl = pendingImageRes?.data?.images?.map((item) => ({
        src: `${API_END_POINT}issues/view/${item?.fileName}`,
    }));

    useEffect(() => {
        if (imgSuccess) {
            const groupedByMonth = groupByMonth(pendingImageRes?.data?.images)
            setPendingList(groupedByMonth)
        }
    }, [pendingImageRes, imgSuccess, isError])

    const getMonthName = (dateStr) => {
        const date = new Date(dateStr);
        const options = { year: 'numeric', month: 'short' }; // e.g. "Apr 2025"
        return date.toLocaleDateString('en-US', options);
    };

    const groupByMonth = (images) => {
        const grouped = {};

        images.forEach((item) => {
            const monthName = getMonthName(item.createdDate);
            if (!grouped[monthName]) {
                grouped[monthName] = [];
            }
            grouped[monthName].push(item);
        });

        return Object.entries(grouped).map(([monthName, data]) => ({
            monthName,
            data,
        }));
    };

    const handleSelectImage = (select) => {
        setSelectedImage((prev) =>
            prev.includes(select)
                ? prev.filter((item) => item !== select)
                : [...prev, select]
        );
    }

    const handleCloseAndOpenApprove = () => {
        setIsImageGalleryApprove(!isImageGalleryApprove)
    }

    const handleCloseAndOpenReject = () => {
        setIsImageGalleryReject(!isImageGalleryReject)
    }

    const handleChangeCategory = (event, value) => {
        if (value != '') {
            let result = categoryList?.data?.[0]?.items?.find((val, index) => val?.value == value);

            setFilters((prev) => ({
                ...prev,
                category: result?.value
            }))
            setCategory(result?.value)
        } else {
            setFilters((prev) => ({
                ...prev,
                category: ''
            }))
            setCategory('')
        }

    }

    const CustomCalendarIcon = (props) => (
        <CalendarIcon {...props} style={{ width: 26, height: 26, fill: "#696969 ", stroke: "#696969", strokeWidth: 4 }} />
    );

    return (
        <>
            <Grid2 container spacing={2} sx={{ mt: "20px" }}>
                <Grid2 size={12}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: { xs: 'start', sm: 'center' },
                            justifyContent: "space-between",
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            sx={{
                                // flexGrow: 2,
                                alignItems: { xs: "stretch", sm: "center" }, // Full width on mobile
                                width: { xs: '100%', sm: 'auto' }, // Stack takes full width
                                "& .MuiStack-root": {
                                    paddingTop: "15px",
                                },
                            }}
                        >
                            <LocalizationProvider dateAdapter={AdapterDayjs} size="small">
                                <DemoContainer components={["DatePicker", "DatePicker"]}>
                                    <DatePicker
                                        label="From Date"
                                        value={fromDate}
                                        format="DD-MM-YYYY"
                                        onChange={(newValue) => setFromDate(newValue)}
                                        slots={{
                                            openPickerIcon: CustomCalendarIcon,
                                        }}
                                        slotProps={{
                                            textField: {
                                                sx: {
                                                    width: { xs: "100%", sm: "100px" },
                                                    "& .MuiOutlinedInput-input": {
                                                        padding: "10px",
                                                    },

                                                    "& .MuiInputLabel-root": {
                                                        top: "-6px",
                                                    },
                                                },
                                            },
                                            field: {
                                                clearable: true,
                                                onClear: () =>
                                                    setFilters({
                                                        fromDate: "",
                                                        toDate: toDate
                                                            ? dayjs(toDate).format("YYYY-MM-DD")
                                                            : "",
                                                    }),
                                            },
                                        }}
                                    />
                                </DemoContainer>
                            </LocalizationProvider>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DemoContainer components={["DatePicker", "DatePicker"]}>
                                    <DatePicker
                                        label="To Date"
                                        value={toDate}
                                        format="DD-MM-YYYY"
                                        clearable={true}
                                        onChange={(newValue) => setToDate(newValue)}
                                        slots={{
                                            openPickerIcon: CustomCalendarIcon,
                                        }}
                                        slotProps={{
                                            textField: {
                                                sx: {
                                                    width: { xs: "100%", sm: "100px" },
                                                    "& .MuiOutlinedInput-input": {
                                                        padding: "10px",
                                                    },

                                                    "& .MuiInputLabel-root": {
                                                        top: "-6px",
                                                    },
                                                },
                                            },
                                            field: {
                                                clearable: true,
                                                onClear: () =>
                                                    setFilters({
                                                        fromDate: fromDate
                                                            ? dayjs(fromDate).format("YYYY-MM-DD")
                                                            : "",
                                                        toDate: "",
                                                    }),
                                            },
                                        }}
                                    />
                                </DemoContainer>
                            </LocalizationProvider>
                            <Button
                                // loading={isLoading}
                                onClick={() => {
                                    setFilters({
                                        fromDate: fromDate
                                            ? dayjs(fromDate).format("YYYY-MM-DD")
                                            : "",
                                        toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : "",
                                        category: category
                                    });
                                }}
                                size="large"
                                // startIcon={<IoMdSearch />}
                                variant="outlined"
                                sx={{
                                    color: `${theme?.customSettings?.themeColor}`,
                                    border: `1px solid ${theme?.customSettings?.themeColor}`,
                                    background: "none",
                                    textTransform: "inherit",
                                    padding: "6px 15px",
                                    marginTop: "12px !important",
                                }}
                            >
                                Search
                            </Button>
                            <Button
                                // loading={isLoading}
                                onClick={() => {
                                    setCategory("");
                                    setFromDate(null)
                                    setToDate(null)
                                    const clearedValues = Object.fromEntries(
                                        Object.keys(filters).map(key => [key, ""])
                                    );
                                    setFilters(clearedValues);
                                }}
                                size="large"
                                variant="outlined"
                                sx={{
                                    color: `${theme?.customSettings?.themeColor}`,
                                    border: `1px solid ${theme?.customSettings?.themeColor}`,
                                    background: "none",
                                    textTransform: "inherit",
                                    padding: "6px 15px",
                                    marginTop: "12px !important",
                                }}
                            >
                                Reset
                            </Button>
                        </Stack>
                    </Box>
                </Grid2>
                <Grid2 size={12}>
                    <Tabs value={filters?.category} onChange={handleChangeCategory} variant="scrollable"
                        scrollButtons="auto"
                        aria-label="scrollable auto tabs example"
                        allowScrollButtonsMobile
                        sx={{
                            mt: 1,
                            minHeight: '0px',
                            backgroundColor: '#fff',
                            borderRadius: '5px',
                            "& .MuiTabs-indicator": {
                                height: '0px'
                            },
                            "& .MuiTabs-scrollButtons.Mui-disabled": {
                                opacity: 0.3
                            },
                            "& .MuiTab-root": {
                                margin: "8px 5px 8px 5px",
                                "&:first-of-type": {
                                    marginLeft: '6px',
                                },
                                "&:last-of-type": {
                                    marginRight: '6px',
                                },
                            },
                        }}
                    >
                        <StyledSubTab key={0} value={''} label={'All'} />
                        {categoryList?.data?.[0]?.items?.map((val, index) => {
                            return <StyledSubTab key={index + 1} value={val?.value} label={val?.value} />
                        })}
                    </Tabs>
                </Grid2>
                <Grid2 size={12}>
                    {pendingList?.length > 0 ?
                        pendingList?.map((val, index) => {
                            return (
                                <>
                                    <Typography variant="h6" sx={{ marginBottom: 1.5 }}>
                                        {val?.monthName}
                                    </Typography>
                                    <ImageList cols={columns} gap={12} sx={{ marginBottom: 5 }}>
                                        {val?.data?.map((item, itemIndex) => (
                                            <ImageListItem key={item.img} sx={{
                                                height: '300px !important', '& .MuiImageListItem-img': {
                                                    borderRadius: '5px',
                                                    height: 'inherit'
                                                }
                                            }}
                                                onClick={() => {
                                                    const index = pendingImageRes?.data?.images?.findIndex(i => i?.id === item?.id);
                                                    setPhotoIndex(index);
                                                    setOpen(true);
                                                }}
                                            >
                                                <img
                                                    srcSet={`${API_END_POINT}issues/view/${item?.fileName}?w=248&fit=crop&auto=format&dpr=2 2x`}
                                                    src={`${API_END_POINT}issues/view/${item?.fileName}?w=248&fit=crop&auto=format`}
                                                    alt={item.title}
                                                    loading="eager"
                                                />
                                                <ImageListItemBar
                                                    subtitle={
                                                        <Box>
                                                            <Typography variant="body2"
                                                                sx={{
                                                                    color: '#E8E8E8',
                                                                    fontSize: '16px',
                                                                    fontWeight: 700,
                                                                    lineHeight: '20px',
                                                                    letterSpacing: '0.01px',
                                                                    mb: 1.5
                                                                }}
                                                            >
                                                                Uploaded on {moment(item?.createdDate).format('DD/MM/YYYY')}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{
                                                                color: '#D9D9D9',
                                                                fontSize: '16px',
                                                                fontWeight: 700,
                                                                lineHeight: '20px',
                                                                letterSpacing: '0.01px'
                                                            }}>
                                                                {item?.createdBy}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    sx={{
                                                        background: 'rgba(105, 105, 105, 0.8)',
                                                        borderRadius: '5px',
                                                        '& .MuiImageListItemBar-title': {
                                                            fontSize: '18px',
                                                            fontWeight: 700,
                                                            lineHeight: '20px',
                                                            letterSpacing: '0.01px',
                                                            color: '#fff',
                                                            mb: 1
                                                        },
                                                    }}
                                                />
                                            </ImageListItem>
                                        ))}
                                    </ImageList>
                                </>
                            )
                        })
                        :
                        <Typography variant="h6" sx={{ fontSize: '14px' }} gutterBottom>
                            {listLoading ?
                                "Loading"
                                : "Record not found."
                            }
                        </Typography>
                    }
                    {open && (
                        <Lightbox
                            open={open}
                            close={() => setOpen(false)}
                            slides={getImageUrl}
                            index={photoIndex}
                        />
                    )}
                </Grid2>
            </Grid2>
        </>
    );
};

export default SubmittedByMeImages;
